import { useQuery } from '@tanstack/react-query';
import { Layers, ChevronRight, TrendingUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import clsx from 'clsx';
import { advisorApi } from '../../api/client';
import { useAppStore } from '../../store/useAppStore';
import { formatOdds } from '../../utils/format';
import type { ParlayRecommendation, ParlayLeg } from '../../types';

function legToParlayLeg(leg: ParlayRecommendation['legs'][number]): ParlayLeg {
  return {
    id:                 uuidv4(),
    eventPk:            leg.eventPk ?? String(leg.gamePk ?? ''),
    leagueId:           leg.leagueId ?? 'mlb',
    gameDate:           leg.gameDate,
    awayTeam:           leg.awayTeam,
    homeTeam:           leg.homeTeam,
    market:             leg.market,
    selection:          leg.selection,
    odds:               leg.odds,
    impliedProbability: leg.impliedProbability,
    modelProbability:   leg.modelProbability,
    edge:               leg.edge,
    confidence:         leg.confidence,
    recommendationId:   leg.id,
  };
}

const LEAGUE_COLORS: Record<string, string> = {
  mlb:  'bg-brand/20 text-brand-light',
  wnba: 'bg-purple-500/20 text-purple-300',
  nba:  'bg-orange-500/20 text-orange-300',
};

interface Props {
  date?: string;
}

export function ParlayRecommendations({ date }: Props) {
  const { addLegToSlip, parlaySlip } = useAppStore();

  const { data: parlays = [], isLoading } = useQuery({
    queryKey: ['advisor-parlays', date],
    queryFn:  () => advisorApi.getParlayRecommendations(date),
    staleTime: 2 * 60_000,
    enabled:   true,
  });

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-[10px] text-gray-600 flex items-center gap-1.5">
        <Layers className="w-3 h-3 animate-pulse" />
        Loading parlay suggestions…
      </div>
    );
  }

  if (parlays.length === 0) return null;

  return (
    <div className="border-t border-surface-4">
      <div className="px-3 py-2 flex items-center gap-1.5">
        <Layers className="w-3 h-3 text-brand" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          AI Parlays
        </span>
        <span className="text-[9px] text-gray-600 ml-auto">{parlays.length} suggested</span>
      </div>

      <div className="space-y-2 px-2 pb-3">
        {parlays.map((parlay) => {
          const allInSlip = parlay.legs.every((l) =>
            parlaySlip.some((s) => s.recommendationId === l.id),
          );

          return (
            <div
              key={parlay.id}
              className="rounded-md border border-surface-4 bg-surface-1/60 overflow-hidden"
            >
              {/* Header row */}
              <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2/40 border-b border-surface-4">
                <span className={clsx(
                  'text-[9px] font-bold px-1.5 py-0.5 rounded border',
                  parlay.decisionStatus === 'STRONG_BET'
                    ? 'bg-win/15 border-win/40 text-win'
                    : 'bg-brand/15 border-brand/40 text-brand-light',
                )}>
                  {parlay.decisionStatus === 'STRONG_BET' ? 'STRONG' : 'QUALIFIED'}
                </span>
                <span className="text-[10px] font-bold text-gray-200 flex-1">
                  {parlay.legs.length}-leg parlay
                </span>
                <span className={clsx(
                  'text-sm font-bold',
                  parlay.combinedOdds > 0 ? 'text-win' : 'text-gray-300',
                )}>
                  {formatOdds(parlay.combinedOdds)}
                </span>
              </div>

              {/* Legs */}
              <div className="divide-y divide-surface-4/60">
                {parlay.legs.map((leg) => (
                  <div key={leg.id} className="flex items-center gap-2 px-2.5 py-1.5">
                    <span className={clsx(
                      'text-[8px] font-bold uppercase px-1 py-0.5 rounded',
                      LEAGUE_COLORS[leg.leagueId ?? 'mlb'] ?? 'bg-surface-3 text-gray-400',
                    )}>
                      {leg.leagueId ?? 'mlb'}
                    </span>
                    <span className="text-[10px] text-gray-300 flex-1 truncate">{leg.selection}</span>
                    <span className={clsx(
                      'text-[10px] font-semibold',
                      leg.odds > 0 ? 'text-win' : 'text-gray-400',
                    )}>
                      {formatOdds(leg.odds)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Edge + take button */}
              <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2/30 border-t border-surface-4">
                {parlay.combinedEdge > 0 && (
                  <span className="text-[10px] text-win flex items-center gap-0.5">
                    <TrendingUp className="w-3 h-3" />
                    +{(parlay.combinedEdge * 100).toFixed(1)}% edge
                  </span>
                )}
                <button
                  onClick={() => {
                    if (!allInSlip) {
                      parlay.legs.forEach((l) => addLegToSlip(legToParlayLeg(l)));
                    }
                  }}
                  disabled={allInSlip}
                  className={clsx(
                    'ml-auto flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded transition-colors',
                    allInSlip
                      ? 'bg-win/20 text-win cursor-default'
                      : 'bg-brand/20 hover:bg-brand/30 text-brand-light',
                  )}
                >
                  {allInSlip ? 'Added ✓' : 'Take Parlay'}
                  {!allInSlip && <ChevronRight className="w-3 h-3" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
